import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiDownload, apiMaybe, apiRequest } from '../../../services/apiClient';
import useUrlSyncedFilters, { useReseedPageGuard } from '../../../hooks/useUrlSyncedFilters';
import { DATE_RANGE_OPTIONS } from './activityMeta';

export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function getStoredPageSize() {
  const stored = parseInt(localStorage.getItem('activityPageSize'), 10);
  return PAGE_SIZE_OPTIONS.includes(stored) ? stored : 50;
}

function parsePage(value) {
  const page = parseInt(value, 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
}

// The ONE definition of this log's filters given a querystring — shared by the
// mount seeding and the external-change re-seed (doc-195) so the two paths
// cannot drift. Parity note: `range` is read raw here, matching what the
// initializers did before — validating it against DATE_RANGE_OPTIONS is
// doc-194's job on this surface, not this change's.
function seedFromParams(params) {
  return {
    q: params.get('q') || '',
    userId: params.get('userId') || '',
    action: params.get('action') || '',
    entityType: params.get('entityType') || '',
    range: params.get('range') || '',
    customFrom: params.get('from') || '',
    customTo: params.get('to') || '',
    errorsOnly: params.get('errors') === 'true',
    page: parsePage(params.get('page')),
  };
}

// Identity of "which filters are applied", for the page-reset guard. The reset
// effect watches debouncedQ rather than q, and a re-seed sets both from the same
// value, so `q` here stands for both — see the re-seed comment below.
function filtersKeyOf({ q, userId, action, entityType, range, customFrom, customTo, errorsOnly }) {
  return JSON.stringify([q, userId, action, entityType, range, customFrom, customTo, errorsOnly]);
}

// Translate the range preset (+ custom dates) into ISO from/to bounds.
// created_at is stored UTC, so day boundaries are computed in UTC.
function rangeBounds(range, customFrom, customTo) {
  if (range === 'custom') {
    return {
      from: customFrom ? `${customFrom}T00:00:00.000Z` : undefined,
      to: customTo ? `${customTo}T23:59:59.999Z` : undefined,
    };
  }
  const preset = DATE_RANGE_OPTIONS.find((o) => o.value === range);
  if (preset?.days) {
    return { from: new Date(Date.now() - preset.days * 86400000).toISOString(), to: undefined };
  }
  return { from: undefined, to: undefined };
}

export default function useActivityLog({ enabled = true } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [seed] = useState(() => seedFromParams(searchParams));
  const [q, setQ] = useState(seed.q);
  const [debouncedQ, setDebouncedQ] = useState(seed.q);
  const [userId, setUserId] = useState(seed.userId);
  const [action, setAction] = useState(seed.action);
  const [entityType, setEntityType] = useState(seed.entityType);
  const [range, setRange] = useState(seed.range);
  const [customFrom, setCustomFrom] = useState(seed.customFrom);
  const [customTo, setCustomTo] = useState(seed.customTo);
  const [errorsOnly, setErrorsOnly] = useState(seed.errorsOnly);
  const [page, setPage] = useState(seed.page);
  const [pageSize, setPageSize] = useState(getStoredPageSize);

  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, page: 1, pageSize });
  const [actions, setActions] = useState([]);
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Filter-option dropdowns — loaded once. apiMaybe resolves null on failure
  // (and logs it); the dropdowns just stay empty.
  useEffect(() => {
    if (!enabled) return;
    apiMaybe('/admin/activity/actions').then((d) => setActions(d?.actions || []));
    apiMaybe('/admin/activity/actors').then((d) => setActors(d?.actors || []));
  }, [enabled]);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Any filter change resets to the first page — but NOT on mount, or a
  // deep-linked ?page=3 gets clobbered before the first fetch (doc-192); and not
  // when the change is a re-seed landing, which carries its own page (doc-224).
  // Both guards, in this order, are the idiom doc-224 shipped on RequestsList /
  // ReviewsList — the mount guard first, so a mount never consumes a pending
  // re-seed key (nothing can have marked one: useUrlSyncedFilters adopts the
  // mount querystring instead of calling onExternalChange). NOTE: React 18
  // StrictMode double-invokes effects in CRA DEV and consumes the mount guard, so
  // deep links look broken on the dev server and are correct in a production
  // build — judge this by unit test or a built bundle, never `npm start`.
  const { markReseeded, isReseedArrival } = useReseedPageGuard();
  const filtersTouchedRef = useRef(false);
  useEffect(() => {
    if (!filtersTouchedRef.current) {
      filtersTouchedRef.current = true;
      return;
    }
    const key = filtersKeyOf({ q: debouncedQ, userId, action, entityType, range, customFrom, customTo, errorsOnly });
    if (isReseedArrival(key)) return;
    setPage(1);
  }, [debouncedQ, userId, action, entityType, range, customFrom, customTo, errorsOnly, isReseedArrival]);

  // Re-seed when the querystring changes from OUTSIDE this hook — the sidebar
  // "Activity" link while already on /admin/activity, a CommandK jump. Without
  // it the URL blanks and every control keeps its old value (doc-195).
  // debouncedQ is re-seeded alongside q on purpose: the URL-sync effect below
  // writes debouncedQ, so re-seeding only q would let that effect put the OLD
  // search term straight back into the freshly-blanked URL.
  const markUrlWritten = useUrlSyncedFilters({
    searchParams,
    enabled,
    onExternalChange: useCallback((params) => {
      const next = seedFromParams(params);
      // Before the state updates, so the page-reset effect recognises the
      // filter change they cause and leaves next.page alone.
      markReseeded(filtersKeyOf(next));
      setQ(next.q);
      setDebouncedQ(next.q);
      setUserId(next.userId);
      setAction(next.action);
      setEntityType(next.entityType);
      setRange(next.range);
      setCustomFrom(next.customFrom);
      setCustomTo(next.customTo);
      setErrorsOnly(next.errorsOnly);
      setPage(next.page);
    }, [markReseeded]),
  });

  // Keep the URL in sync so a filtered view survives refresh / is shareable.
  // Gated on `enabled` like the two effects around it (doc-186): AdminActivity
  // renders <Navigate to="/"> for anyone without view_audit, but hooks run
  // whatever render returned and child effects run first — an unguarded write
  // here replaces the URL straight back to /admin/activity after the redirect,
  // the route re-matches, the gate redirects again, and nothing ever renders.
  // `enabled` MUST stay in the deps too: it is false while auth loads, and none
  // of the other filter deps change when it flips true, so omitting it means an
  // authorized admin silently loses URL sync for the whole session.
  useEffect(() => {
    if (!enabled) return;
    const params = new URLSearchParams();
    if (debouncedQ) params.set('q', debouncedQ);
    if (userId) params.set('userId', userId);
    if (action) params.set('action', action);
    if (entityType) params.set('entityType', entityType);
    if (range) params.set('range', range);
    if (range === 'custom' && customFrom) params.set('from', customFrom);
    if (range === 'custom' && customTo) params.set('to', customTo);
    if (errorsOnly) params.set('errors', 'true');
    if (page > 1) params.set('page', String(page));
    setSearchParams(params, { replace: true });
    // Record what WE wrote so the re-seed above ignores our own write.
    markUrlWritten(params);
  }, [enabled, debouncedQ, userId, action, entityType, range, customFrom, customTo, errorsOnly, page, setSearchParams, markUrlWritten]);

  // Fetch the page. Stale-response guard: a slower earlier request must not
  // clobber the latest after rapid filter changes.
  useEffect(() => {
    if (!enabled) return undefined;
    const { from, to } = rangeBounds(range, customFrom, customTo);
    const params = new URLSearchParams();
    if (debouncedQ) params.set('q', debouncedQ);
    if (userId) params.set('userId', userId);
    if (action) params.set('action', action);
    if (entityType) params.set('entityType', entityType);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (errorsOnly) params.set('errors', 'true');
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));

    setLoading(true);
    let cancelled = false;
    apiRequest(`/admin/activity?${params.toString()}`)
      .then((data) => {
        if (cancelled) return;
        setEntries(data.entries || []);
        setPagination(data.pagination || { total: 0, totalPages: 1, page: 1, pageSize });
        // ?page= comes from the URL so it can be out of range, and the server
        // CLAMPS rather than 404s (admin.js, doc-215), reporting the page it
        // actually served. Adopt it, or a stale shared ?page=999 renders
        // "999 / 2" and "24951–36 of 36" over the last page's rows (doc-231) —
        // both strings are drawn from this local `page`. Setting it re-runs the
        // URL-sync effect above, which self-heals the querystring to ?page=2
        // (and marks the write, so the re-seed watcher ignores it), and re-runs
        // this fetch once for the served page — where servedPage === page and
        // the adoption stops.
        const servedPage = data.pagination?.page;
        if (servedPage && servedPage !== page) setPage(servedPage);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [enabled, debouncedQ, userId, action, entityType, range, customFrom, customTo, errorsOnly, page, pageSize, reloadKey]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    localStorage.setItem('activityPageSize', String(newSize));
    setPage(1);
  };

  // Filter-aware CSV export (doc-116): same filters as the table, no paging —
  // the download is every matching row. Errors propagate for the page to toast.
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    const { from, to } = rangeBounds(range, customFrom, customTo);
    const params = new URLSearchParams();
    if (debouncedQ) params.set('q', debouncedQ);
    if (userId) params.set('userId', userId);
    if (action) params.set('action', action);
    if (entityType) params.set('entityType', entityType);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (errorsOnly) params.set('errors', 'true');
    setExporting(true);
    try {
      await apiDownload(`/admin/activity/export.csv?${params}`, 'docket-activity.csv');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setQ('');
    setUserId('');
    setAction('');
    setEntityType('');
    setRange('');
    setCustomFrom('');
    setCustomTo('');
    setErrorsOnly(false);
  };

  const hasFilters = !!(debouncedQ || userId || action || entityType || range || errorsOnly);
  const refresh = () => setReloadKey((k) => k + 1);

  return {
    q, setQ,
    userId, setUserId,
    action, setAction,
    entityType, setEntityType,
    range, setRange,
    customFrom, setCustomFrom,
    customTo, setCustomTo,
    errorsOnly, setErrorsOnly,
    page, setPage,
    pageSize, pageSizeOptions: PAGE_SIZE_OPTIONS, handlePageSizeChange,
    entries, pagination, actions, actors,
    loading, error,
    exportCsv, exporting,
    hasFilters, clearFilters, refresh,
  };
}
